import apiClient from "./client";
import type { PublicStats, TrendingKeyword } from "@/types/common";
import type { ThesisListItem } from "@/types/thesis";

// ─── Admin Stats Types ───────────────────────────────────────────────────────

export interface OverviewStats {
  total_theses: number;
  total_documents: number;
  total_authors: number;
  total_users: number;
  total_partners: number;
  pending_theses: number;
  pending_documents: number;
  pending_claims: number;
  total_reviews: number;
  total_reading_sessions_today: number;
  total_page_views_today: number;
  theses_change_pct: number | null;
  documents_change_pct: number | null;
  users_change_pct: number | null;
  reviews_change_pct: number | null;
  sessions_change_pct: number | null;
}

export interface PopularThesis {
  thesis_id: string;
  title: string;
  author_name: string;
  department: string;
  views: number;
  unique_viewers: number;
  avg_time_spent: number;
}

export interface PopularDocument {
  document_id: string;
  title: string; // already resolved (fr or en)
  document_type: string;
  section_slug: string;
  authors: string; // comma-separated author names
  views: number;
  unique_viewers: number;
  avg_time_spent: number;
}

export interface TimeSeriesPoint {
  date: string;
  count: number;
}

export interface DepartmentStat {
  department_id: string;
  department_name: string;
  thesis_count: number;
}

export interface DegreeStat {
  degree_level: string;
  count: number;
}

export interface TypeStat {
  thesis_type: string;
  count: number;
}

export interface SectionStat {
  section_id: string;
  section_name: string;
  document_count: number;
}

export interface RoleStat {
  role: string;
  count: number;
}

export interface HourStat {
  hour: number;
  session_count: number;
}

export interface ReviewStats {
  total_reviews: number;
  average_rating: number;
  reviews_this_month: number;
  rating_distribution: {
    "1": number;
    "2": number;
    "3": number;
    "4": number;
    "5": number;
  };
}

export type StatPeriod = "week" | "month" | "year" | "all_time";

// ─── Public Stats ────────────────────────────────────────────────────────────

/**
 * Get public statistics for the homepage (total theses, authors, departments).
 *
 * Maps to `GET /api/stats/public`
 */
export async function getPublicStats(): Promise<PublicStats> {
  const { data } = await apiClient.get<PublicStats>("/stats/public");
  return data;
}

// ─── Trending Keywords ───────────────────────────────────────────────────────

/**
 * Get trending keywords aggregated from recently approved theses.
 *
 * Maps to `GET /api/stats/trending-keywords`
 */
export async function getTrendingKeywords(
  limit: number = 20,
): Promise<TrendingKeyword[]> {
  const { data } = await apiClient.get<TrendingKeyword[]>(
    "/stats/trending-keywords",
    { params: { limit } },
  );
  return data;
}

// ─── Discovery Helpers ───────────────────────────────────────────────────────

/**
 * Get recently added theses (approved, sorted newest first).
 *
 * Convenience wrapper around the theses list endpoint.
 * Maps to `GET /api/theses?sort_by=date&sort_order=desc&status=approved&per_page=:limit`
 */
export async function getRecentTheses(
  limit: number = 8,
): Promise<ThesisListItem[]> {
  const { data } = await apiClient.get<{
    data: ThesisListItem[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>("/theses", {
    params: {
      sort_by: "date",
      sort_order: "desc",
      status: "approved",
      per_page: limit,
      page: 1,
    },
  });
  return data.data;
}

// ─── Admin Stats API ─────────────────────────────────────────────────────────

/**
 * Get admin overview stats with change percentages.
 *
 * Maps to `GET /api/stats/overview`
 */
export async function getOverviewStats(): Promise<OverviewStats> {
  const { data } = await apiClient.get<OverviewStats>("/stats/overview");
  return data;
}

/**
 * Get most popular theses within a period.
 *
 * Maps to `GET /api/stats/popular?period=month&limit=10`
 */
export async function getPopularThesesStats(
  period: StatPeriod = "month",
  limit: number = 10,
): Promise<PopularThesis[]> {
  const { data } = await apiClient.get<PopularThesis[]>("/stats/popular", {
    params: { period, limit },
  });
  return data;
}

/**
 * Get thesis view counts over time.
 *
 * Maps to `GET /api/stats/thesis-views?period=month`
 */
export async function getThesisViews(
  period: StatPeriod = "month",
): Promise<TimeSeriesPoint[]> {
  const { data } = await apiClient.get<TimeSeriesPoint[]>(
    "/stats/thesis-views",
    { params: { period } },
  );
  return data;
}

/**
 * Get thesis counts per department.
 *
 * Maps to `GET /api/stats/theses-by-department`
 */
export async function getThesesByDepartment(): Promise<DepartmentStat[]> {
  const { data } = await apiClient.get<DepartmentStat[]>(
    "/stats/theses-by-department",
  );
  return data;
}

/**
 * Get thesis counts per degree level.
 *
 * Maps to `GET /api/stats/theses-by-degree`
 */
export async function getThesesByDegree(): Promise<DegreeStat[]> {
  const { data } = await apiClient.get<DegreeStat[]>("/stats/theses-by-degree");
  return data;
}

/**
 * Get thesis counts per type (physical, digital, both).
 *
 * Maps to `GET /api/stats/theses-by-type`
 */
export async function getThesesByType(): Promise<TypeStat[]> {
  const { data } = await apiClient.get<TypeStat[]>("/stats/theses-by-type");
  return data;
}

/**
 * Get user registrations over time.
 *
 * Maps to `GET /api/stats/user-registrations?period=month`
 */
export async function getUserRegistrations(
  period: StatPeriod = "month",
): Promise<TimeSeriesPoint[]> {
  const { data } = await apiClient.get<TimeSeriesPoint[]>(
    "/stats/user-registrations",
    { params: { period } },
  );
  return data;
}

/**
 * Get active users over time.
 *
 * Maps to `GET /api/stats/active-users?period=month`
 */
export async function getActiveUsers(
  period: StatPeriod = "month",
): Promise<TimeSeriesPoint[]> {
  const { data } = await apiClient.get<TimeSeriesPoint[]>(
    "/stats/active-users",
    { params: { period } },
  );
  return data;
}

/**
 * Get user counts per role.
 *
 * Maps to `GET /api/stats/users-by-role`
 */
export async function getUsersByRole(): Promise<RoleStat[]> {
  const { data } = await apiClient.get<RoleStat[]>("/stats/users-by-role");
  return data;
}

/**
 * Get reading session counts over time.
 *
 * Maps to `GET /api/stats/reading-sessions?period=month`
 */
export async function getReadingSessions(
  period: StatPeriod = "month",
): Promise<TimeSeriesPoint[]> {
  const { data } = await apiClient.get<TimeSeriesPoint[]>(
    "/stats/reading-sessions",
    { params: { period } },
  );
  return data;
}

/**
 * Get pages viewed over time.
 *
 * Maps to `GET /api/stats/pages-viewed?period=month`
 */
export async function getPagesViewed(
  period: StatPeriod = "month",
): Promise<TimeSeriesPoint[]> {
  const { data } = await apiClient.get<TimeSeriesPoint[]>(
    "/stats/pages-viewed",
    { params: { period } },
  );
  return data;
}

/**
 * Get peak reading hours (sessions aggregated by hour of day).
 *
 * Maps to `GET /api/stats/peak-hours`
 */
export async function getPeakHours(): Promise<HourStat[]> {
  const { data } = await apiClient.get<HourStat[]>("/stats/peak-hours");
  return data;
}

/**
 * Get review statistics with rating distribution.
 *
 * Maps to `GET /api/stats/reviews`
 */
export async function getReviewStats(): Promise<ReviewStats> {
  const { data } = await apiClient.get<ReviewStats>("/stats/reviews");
  return data;
}

/**
 * Get most popular theses (approved, sorted by views count descending).
 *
 * Maps to `GET /api/theses?sort_by=views&sort_order=desc&status=approved&per_page=:limit`
 */
export async function getPopularTheses(
  limit: number = 4,
): Promise<ThesisListItem[]> {
  const { data } = await apiClient.get<{
    data: ThesisListItem[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>("/theses", {
    params: {
      sort_by: "views",
      sort_order: "desc",
      status: "approved",
      per_page: limit,
      page: 1,
    },
  });
  return data.data;
}

/**
 * Get top-rated theses (approved, sorted by average rating descending).
 *
 * Maps to `GET /api/theses?sort_by=rating&sort_order=desc&status=approved&per_page=:limit`
 */
export async function getTopRatedTheses(
  limit: number = 6,
): Promise<ThesisListItem[]> {
  const { data } = await apiClient.get<{
    data: ThesisListItem[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>("/theses", {
    params: {
      sort_by: "rating",
      sort_order: "desc",
      status: "approved",
      per_page: limit,
      page: 1,
    },
  });
  return data.data;
}

/**
 * Get document view counts over time (all document types).
 * Maps to `GET /api/stats/document-views?period=month`
 */
export async function getDocumentViews(
  period: StatPeriod = "month",
): Promise<TimeSeriesPoint[]> {
  const { data } = await apiClient.get<TimeSeriesPoint[]>(
    "/stats/document-views",
    { params: { period } },
  );
  return data;
}

/**
 * Get document counts per section.
 * Maps to `GET /api/stats/documents-by-section`
 */
export async function getDocumentsBySection(): Promise<SectionStat[]> {
  const { data } = await apiClient.get<SectionStat[]>(
    "/stats/documents-by-section",
  );
  return data;
}

/**
 * Get document counts per type.
 * Maps to `GET /api/stats/documents-by-type`
 */
export async function getDocumentsByType(): Promise<TypeStat[]> {
  const { data } = await apiClient.get<TypeStat[]>("/stats/documents-by-type");
  return data;
}

/**
 * Get most popular documents (all types) within a period.
 * Maps to `GET /api/stats/popular-documents?period=month&limit=10`
 */
export async function getPopularDocumentsStats(
  period: StatPeriod = "month",
  limit: number = 10,
): Promise<PopularDocument[]> {
  const { data } = await apiClient.get<PopularDocument[]>(
    "/stats/popular-documents",
    { params: { period, limit } },
  );
  return data;
}

/**
 * Get recently added generic documents (approved, sorted newest first).
 * Maps to `GET /api/documents?sort_by=date&sort_order=desc&status=approved&per_page=:limit`
 */
export async function getRecentDocuments(
  limit: number = 8,
): Promise<import("@/types/document").DocumentListItem[]> {
  const { data } = await apiClient.get<{
    data: import("@/types/document").DocumentListItem[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>("/documents", {
    params: {
      sort_by: "date",
      sort_order: "desc",
      status: "approved",
      per_page: limit,
      page: 1,
    },
  });
  return data.data;
}

/**
 * Get most popular generic documents (approved, sorted by views descending).
 * Maps to `GET /api/documents?sort_by=views&sort_order=desc&status=approved&per_page=:limit`
 */
export async function getPopularDocuments(
  limit: number = 4,
): Promise<import("@/types/document").DocumentListItem[]> {
  const { data } = await apiClient.get<{
    data: import("@/types/document").DocumentListItem[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>("/documents", {
    params: {
      sort_by: "views",
      sort_order: "desc",
      status: "approved",
      per_page: limit,
      page: 1,
    },
  });
  return data.data;
}

/**
 * Get top-rated generic documents (approved, sorted by rating descending).
 * Maps to `GET /api/documents?sort_by=rating&sort_order=desc&status=approved&per_page=:limit`
 */
export async function getTopRatedDocuments(
  limit: number = 6,
): Promise<import("@/types/document").DocumentListItem[]> {
  const { data } = await apiClient.get<{
    data: import("@/types/document").DocumentListItem[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  }>("/documents", {
    params: {
      sort_by: "rating",
      sort_order: "desc",
      status: "approved",
      per_page: limit,
      page: 1,
    },
  });
  return data.data;
}
